import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

import Loading from "@/components/Common/Loading";
import Page from "@/components/Common/Page";
import FacilityConsultationChargeSelector from "@/components/Facility/FacilityConsultationChargeSelector";

import mutate from "@/Utils/request/mutate";
import useCurrentFacility from "@/pages/Facility/utils/useCurrentFacility";
import { AppointmentPaymentGateMode } from "@/types/facility/facility";
import facilityApi from "@/types/facility/facilityApi";

export function BillingSettings() {
  const { t } = useTranslation();
  const { facility, facilityId } = useCurrentFacility();
  const [editing, setEditing] = useState(false);
  const [expression, setExpression] = useState("");
  const [paymentGateMode, setPaymentGateMode] = useState(
    AppointmentPaymentGateMode.AFTER_APPOINTMENT,
  );

  const queryClient = useQueryClient();

  const {
    mutate: saveExpression,
    isPending,
    isSuccess,
    isError,
    reset,
  } = useMutation({
    mutationFn: mutate(facilityApi.setInvoiceExpression, {
      pathParams: { facilityId },
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["facility", facilityId] });
      setEditing(false);
    },
  });

  const {
    mutate: savePaymentGateMode,
    isPending: isSavingPaymentGateMode,
    isSuccess: isPaymentGateModeSaved,
    isError: isPaymentGateModeError,
    reset: resetPaymentGateModeState,
  } = useMutation({
    mutationFn: mutate(facilityApi.setAppointmentPaymentGateMode, {
      pathParams: { facilityId },
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["facility", facilityId] });
    },
  });

  React.useEffect(() => {
    if (facility && !editing)
      setExpression(facility.invoice_number_expression || "");
  }, [facility, editing]);

  React.useEffect(() => {
    if (facility) {
      setPaymentGateMode(facility.appointment_payment_gate_mode);
    }
  }, [facility]);

  if (!facility) {
    return <Loading />;
  }

  return (
    <Page title={t("invoice_number_expression")}>
      <section className="w-full max-w-5xl mx-auto mt-8">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-8 flex flex-col gap-6">
          <div className="flex flex-col gap-4 border-b border-gray-200 pb-6">
            <div className="flex flex-col gap-2">
              <h2 className="text-xl font-semibold text-gray-900">
                {t("appointment_payment_gate_timing")}
              </h2>
              <p className="text-gray-500 text-sm">
                {t("appointment_payment_gate_timing_description")}
              </p>
            </div>

            <RadioGroup
              value={paymentGateMode}
              onValueChange={(value) =>
                setPaymentGateMode(value as AppointmentPaymentGateMode)
              }
              className="grid gap-3"
            >
              <div className="flex items-start gap-3 rounded-lg border border-gray-200 p-4">
                <RadioGroupItem
                  value={AppointmentPaymentGateMode.BEFORE_APPOINTMENT}
                  id="before_appointment"
                  className="mt-0.5"
                />
                <div className="space-y-1">
                  <Label htmlFor="before_appointment" className="font-semibold">
                    {t("appointment_payment_gate_before_appointment")}
                  </Label>
                  <p className="text-sm text-gray-500">
                    {t(
                      "appointment_payment_gate_before_appointment_description",
                    )}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 rounded-lg border border-gray-200 p-4">
                <RadioGroupItem
                  value={AppointmentPaymentGateMode.AFTER_APPOINTMENT}
                  id="after_appointment"
                  className="mt-0.5"
                />
                <div className="space-y-1">
                  <Label htmlFor="after_appointment" className="font-semibold">
                    {t("appointment_payment_gate_after_appointment")}
                  </Label>
                  <p className="text-sm text-gray-500">
                    {t(
                      "appointment_payment_gate_after_appointment_description",
                    )}
                  </p>
                </div>
              </div>
            </RadioGroup>

            <div className="flex flex-col gap-2 sm:flex-row sm:gap-2 w-full">
              <Button
                onClick={() =>
                  savePaymentGateMode({
                    appointment_payment_gate_mode: paymentGateMode,
                  })
                }
                disabled={
                  isSavingPaymentGateMode ||
                  paymentGateMode === facility.appointment_payment_gate_mode
                }
                className="w-full sm:w-auto"
              >
                {isSavingPaymentGateMode ? t("saving") : t("save")}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setPaymentGateMode(facility.appointment_payment_gate_mode);
                  resetPaymentGateModeState();
                }}
                className="w-full sm:w-auto"
              >
                {t("cancel")}
              </Button>
            </div>

            {isPaymentGateModeSaved && !isSavingPaymentGateMode && (
              <div className="text-green-600 text-sm">
                {t("saved_successfully")}
              </div>
            )}
            {isPaymentGateModeError && (
              <div className="text-red-600 text-sm">{t("error")}</div>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <h2 className="text-xl font-semibold text-gray-900">
              {t("invoice_number_expression")}
            </h2>
            <div className="text-gray-500 text-sm mt-1">
              <div className="font-semibold mb-1">
                {t("invoice_number_expression_supported_variables")}
              </div>
              <ul className="list-disc list-inside mb-2">
                <li>{t("invoice_number_expression_invoice_count")}</li>
                <li>{t("invoice_number_expression_current_year_yy")}</li>
                <li>{t("invoice_number_expression_current_year_yyyy")}</li>
              </ul>
              <div className="mb-1">
                {t("invoice_number_expression_other_characters")}
              </div>
              <div className="mb-1">{t("arithmetic_help")}</div>
              <pre className="bg-gray-100 rounded px-3 py-2 text-xs font-mono text-gray-700 overflow-x-auto">
                {t("invoice_number_expression_example_value")}
              </pre>
            </div>
          </div>
          <div className="flex flex-col gap-4 w-full max-w-md">
            {editing ? (
              <>
                <Input
                  value={expression}
                  onChange={(e) => setExpression(e.target.value)}
                  className="w-full text-base"
                  autoFocus
                  aria-label={t("invoice_number_expression")}
                />
                <div className="flex flex-col gap-2 sm:flex-row sm:gap-2 w-full">
                  <Button
                    onClick={() =>
                      saveExpression({ invoice_number_expression: expression })
                    }
                    disabled={isPending || !expression.trim()}
                    className="w-full sm:w-auto"
                  >
                    {isPending ? t("saving") : t("save")}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditing(false);
                      setExpression(facility.invoice_number_expression || "");
                      reset();
                    }}
                    className="w-full sm:w-auto"
                  >
                    {t("cancel")}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <span className="font-mono bg-gray-100 px-3 py-2 rounded text-base border border-gray-200 min-w-[200px] text-gray-800 w-full block">
                  {facility.invoice_number_expression || (
                    <span className="text-gray-400">-</span>
                  )}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setEditing(true)}
                  className="w-full sm:w-auto mt-2"
                >
                  {t("edit")}
                </Button>
              </>
            )}
          </div>
          {isSuccess && !editing && (
            <div className="text-green-600 text-sm mt-2">
              {t("saved_successfully")}
            </div>
          )}
          {isError && (
            <div className="text-red-600 text-sm mt-2">{t("error")}</div>
          )}
          <div className="flex items-center justify-between gap-4 border-t border-gray-200 pt-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Consultation charge
              </h2>
              <p className="text-sm text-gray-500">
                Configure the charge used for OPD consultation billing,
                including walk-in encounters.
              </p>
            </div>
            <FacilityConsultationChargeSelector facility={facility} />
          </div>
        </div>
      </section>
    </Page>
  );
}
